import React from 'react';
import Box from '@amzn/awsui-components-console/box';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Badge, { BadgeProps } from '@amzn/awsui-components-console/badge';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-console/status-indicator';
import { ValidationExecutionStatus, ValidationReport, ValidationResult } from 'src/@types/validationTypes';
import ShowSnowStatus, { getStatusConfig } from './ShowSNOWStatus';

interface ValidationRecordProps {
	validationExecutionId?: string;
	validationStatus?: string;
	validationMessage?: string;
	isValidationPolling?: boolean;
	validationReport?: ValidationReport;
	validationResults?: ValidationResult[];
}

const PASSED_STATUSES: string[] = [
	ValidationExecutionStatus.VALIDATION_PASSED,
	ValidationExecutionStatus.VALID,
];

const FAILED_STATUSES: string[] = [
	ValidationExecutionStatus.VALIDATION_FAILED,
	ValidationExecutionStatus.INVALID,
	ValidationExecutionStatus.FAILED,
];

const FATAL_STATUSES: string[] = [ValidationExecutionStatus.VALIDATION_FATAL];

const IN_PROGRESS_STATUSES: string[] = [
	ValidationExecutionStatus.VALIDATION_IN_PROGRESS,
	ValidationExecutionStatus.PENDING,
];

const KNOWN_STATUSES: string[] = [
	...PASSED_STATUSES,
	...FAILED_STATUSES,
	...FATAL_STATUSES,
	...IN_PROGRESS_STATUSES,
];

type ResultOutcome = 'failed' | 'passed' | 'inProgress';

const UNKNOWN_STATUS = 'Unknown status';

/**
 * Resolves the status passed to ShowSnowStatus. Known statuses are shown as-is,
 * consistent with the rest of the app; unrecognized statuses are shown as
 * "Unknown status" so raw enum names never surface in the UI.
 */
const getOverallStatus = (status: string | undefined, isPolling: boolean | undefined): string => {
	if (status && KNOWN_STATUSES.includes(status)) {
		return status;
	}
	if (isPolling) {
		return ValidationExecutionStatus.VALIDATION_IN_PROGRESS;
	}
	return UNKNOWN_STATUS;
};

const classifyResult = (status: string | undefined): ResultOutcome => {
	const normalized = (status || '').toUpperCase();
	if (['FAILED', 'FAIL', 'INVALID', 'ERROR'].includes(normalized)) {
		return 'failed';
	}
	if (['PASSED', 'PASS', 'VALID', 'SUCCESS', 'SUCCEEDED'].includes(normalized)) {
		return 'passed';
	}
	return 'inProgress';
};

/**
 * The errorMessage field is a plain string in the contract, but the SDS
 * (Step Functions) validation path stores a JSON-stringified payload of the
 * shape { validationResults: [{ errors: [...] }], entityName }.
 */
const extractErrorMessages = (raw: string | undefined): string[] => {
	if (!raw) {
		return [];
	}
	try {
		const parsed = JSON.parse(raw);
		// Not the SDS shape: surface the raw string so no error text is lost.
		if (!Array.isArray(parsed?.validationResults)) {
			return [raw];
		}
		// SDS shape: return only the extracted errors, never the raw JSON blob.
		return parsed.validationResults.flatMap((entry: { errors?: unknown }) =>
			Array.isArray(entry?.errors)
				? entry.errors.filter((error): error is string => typeof error === 'string')
				: []
		);
	} catch {
		return [raw];
	}
};

interface OutcomeMeta {
	label: string;
	// Status key used to look up the shared icon/colour from ShowSNOWStatus.
	statusKey: string;
}

const OUTCOME_META: Record<ResultOutcome, OutcomeMeta> = {
	failed: { label: 'FAILED', statusKey: 'FAILED' },
	passed: { label: 'PASSED', statusKey: 'PASSED' },
	inProgress: { label: 'IN PROGRESS', statusKey: ValidationExecutionStatus.VALIDATION_IN_PROGRESS },
};

/**
 * Renders a single outcome group (FAILED / PASSED / IN PROGRESS) as a section
 * header with a count badge, followed by each validation as a status-icon +
 * bold name + grey subtitle row. Modeled on the CR "Verification Record" view
 * using page-standard Cloudscape components.
 */
const OutcomeGroup: React.FC<{
	outcome: ResultOutcome;
	results: ValidationResult[];
	showWorkflowId: boolean;
}> = ({ outcome, results, showWorkflowId }) => {
	if (results.length === 0) {
		return null;
	}
	const meta = OUTCOME_META[outcome];
	const { type, colorOverride } = getStatusConfig(meta.statusKey) as {
		type: StatusIndicatorProps.Type;
		colorOverride: BadgeProps['color'];
	};
	return (
		<SpaceBetween size="m">
			<Box>
				<Box variant="awsui-key-label" display="inline-block" margin={{ right: 'xs' }}>
					{meta.label}
				</Box>
				<Badge color={colorOverride}>{results.length}</Badge>
			</Box>
			<SpaceBetween size="m">
				{results.map((result, index) => (
					<StatusIndicator
						key={`${result.workflowId ?? ''}:${result.validationId || result.validationName || index}`}
						type={type}
					>
						<Box display="inline-block">
							<Box fontWeight="bold" display="inline">
								{result.validationName || result.validationId || 'Validation'}
							</Box>
							{result.validationId && (
								<Box color="text-body-secondary" fontSize="body-s">
									ID: {result.validationId}
								</Box>
							)}
							{showWorkflowId && result.workflowId && (
								<Box color="text-body-secondary" fontSize="body-s">
									Workflow: {result.workflowId}
								</Box>
							)}
							{extractErrorMessages(result.errorMessage).map((message, messageIndex) => (
								<Box key={messageIndex} color="text-status-error">
									{message}
								</Box>
							))}
						</Box>
					</StatusIndicator>
				))}
			</SpaceBetween>
		</SpaceBetween>
	);
};

const ValidationRecord: React.FC<ValidationRecordProps> = ({
	validationStatus,
	validationMessage,
	isValidationPolling,
	validationReport,
	validationResults,
}) => {
	const overallStatus = getOverallStatus(validationStatus, isValidationPolling);

	const validResults = (validationResults || []).filter(
		(result) => result.validationName || result.errorMessage || result.status
	);

	const grouped: Record<ResultOutcome, ValidationResult[]> = {
		failed: [],
		passed: [],
		inProgress: [],
	};
	validResults.forEach((result) => {
		grouped[classifyResult(result.status)].push(result);
	});

	const showWorkflowId = new Set(validResults.map((result) => result.workflowId)).size > 1;

	const hasResults = validResults.length > 0;
	const isFailure =
		(validationStatus && FAILED_STATUSES.includes(validationStatus)) ||
		(validationStatus && FATAL_STATUSES.includes(validationStatus));

	return (
		<Container header={<Header variant="h2">Validation Record</Header>}>
			<SpaceBetween size="l">
				<div>
					<Box variant="awsui-key-label">Overall status</Box>
					<ShowSnowStatus status={overallStatus} />
				</div>

				{validationReport && (
					<div>
						<Box variant="awsui-key-label">Summary</Box>
						<SpaceBetween direction="horizontal" size="l">
							<StatusIndicator type="success">
								{validationReport.passedValidations} Passed
							</StatusIndicator>
							<StatusIndicator type={validationReport.failedValidations > 0 ? 'error' : 'success'}>
								{validationReport.failedValidations} Failed
							</StatusIndicator>
							<Box color="text-body-secondary">
								{validationReport.totalValidations} Total
							</Box>
						</SpaceBetween>
					</div>
				)}

				{validationMessage && (
					<Box color={isFailure ? 'text-status-error' : 'text-body-secondary'}>
						{validationMessage}
					</Box>
				)}

				{hasResults ? (
					<SpaceBetween size="xl">
						{/* Failed first, then passed, then in-progress — matching the CR ordering. */}
						<OutcomeGroup outcome="failed" results={grouped.failed} showWorkflowId={showWorkflowId} />
						<OutcomeGroup outcome="passed" results={grouped.passed} showWorkflowId={showWorkflowId} />
						<OutcomeGroup outcome="inProgress" results={grouped.inProgress} showWorkflowId={showWorkflowId} />
					</SpaceBetween>
				) : (
					<Box textAlign="center" color="inherit" padding="l">
						<b>No validations available</b>
						<Box variant="p" color="text-body-secondary">
							{isValidationPolling
								? 'Validation is still in progress. Results will appear here once available.'
								: 'There are no individual validations for this entity.'}
						</Box>
					</Box>
				)}
			</SpaceBetween>
		</Container>
	);
};

export default ValidationRecord;
